<script lang="ts">
import { computed, defineComponent, inject } from "vue";
import IconSprite from "@/components/IconSprite.vue";
import Infotext from "@/components/Infotext.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import formatUtils from "@/businesslogic/utils/formatUtils";
import Logger from "js-logger";
import ExtraSelbstbeteiligungService from "@/businesslogic/services/ExtraSelbstbeteiligungService";
import dataUtils from "@/businesslogic/utils/dataUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "AbschlussHinweise",
  components: {
    IconSprite,
    Infotext,
  },
  props: {
    produkt: String,
    zusaetzlicheSbLw: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const piniaStore = useFormStore();
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const { formatCurrency } = formatUtils();
    const { istFhkVerkaufsprodukt } = storeUtils();
    const { gt } = dataUtils();

    const beitragVorKernsanierungJaehrlich = computed(() => {
      if (istFhkVerkaufsprodukt(piniaStore.state)) {
        return formatCurrency(piniaStore.state.ferienhaus.vgv.tarifinformationen.beitragVorKernsanierungJaehrlich);
      }
      return formatCurrency(piniaStore.state.tarifinformationen.beitragVorKernsanierungJaehrlich);
    });

    const beitragVorKernsanierungGemZahlungsweise = computed(() => {
      if (istFhkVerkaufsprodukt(piniaStore.state)) {
        return formatCurrency(
          piniaStore.state.ferienhaus.vgv.tarifinformationen.beitragVorKernsanierungGemZahlungsweise,
        );
      }
      return formatCurrency(piniaStore.state.tarifinformationen.beitragVorKernsanierungGemZahlungsweise);
    });

    const zeigeHinweisSchadenfreiBonus = computed(() => {
      return (
        piniaStore.state.tarifinformationen.vorschaedenAnzahl !== undefined &&
        piniaStore.state.tarifinformationen.vorschaedenAnzahl == 0 &&
        piniaStore.state.verkaufsprodukt == "PHV"
      );
    });

    const zeigeAnpassungsfaktorMeldung = computed(() => {
      const vertragsbeginnJahr = parseInt(piniaStore.state.tarifinformationen.versicherungsbeginn.split(".")[2]);
      const aktuellesJahr = new Date().getFullYear();

      return props.produkt == "vgv" && vertragsbeginnJahr > aktuellesJahr;
    });

    const keineDunkelverarbeitungText = computed(() => {
      const aussteuerungsGruende = [];

      aussteuerungsGruende.push("Anschrift");
      aussteuerungsGruende.push("Baujahr");

      if (piniaStore.state.leerstandAusgesteuert) {
        aussteuerungsGruende.push("Leerstand");
      }
      if (piniaStore.state.gewerbeArtAusgesteuert) {
        aussteuerungsGruende.push("Gewerbeart");
      }
      if (piniaStore.state.sanierungAusgesteuert) {
        aussteuerungsGruende.push("Sanierung");
      }
      if (piniaStore.state.denkmalschutzAusgesteuert) {
        aussteuerungsGruende.push("Denkmalschutz");
      }

      Logger.debug("ausGruendenAusgesteuert", aussteuerungsGruende.length, props.produkt);

      if (aussteuerungsGruende.length > 0 && props.produkt == "vgv") {
        return (
          "Aufgrund Ihrer Angaben (z.B. " +
          aussteuerungsGruende.join(", ") +
          ") prüfen wir Ihren Antrag individuell. Hierdurch können sich Abweichungen zum Antrag ergeben."
        );
      }

      return "Aufgrund Ihrer Angaben prüfen wir Ihren Antrag individuell. Hierdurch können sich Abweichungen zum Antrag ergeben.";
    });

    const gebaeudedaten = piniaStore.state.gebaeudedaten;
    const istHinweisFuerBlBh = computed(() => {
      if (piniaStore.state.verkaufsprodukt === "BH") {
        if (gebaeudedaten.wandAnWand === true || gebaeudedaten.durchDritteBewohnt === true) {
          return true;
        }
      }

      if (piniaStore.state.verkaufsprodukt === "BL") {
        return gebaeudedaten.wandAnWand || bausummeFuerGebaeudetypUeberschritten.value;
      }

      return false;
    });

    const bausummeFuerGebaeudetypUeberschritten = computed(() => {
      const gebaeudetyp = gebaeudedaten.gebaeudetyp;
      const bausumme = gebaeudedaten.bausumme;
      return (gebaeudetyp === "8" && gt(bausumme, 1000000)) || (gebaeudetyp === "3" && gt(bausumme, 2400000));
    });

    const istDunkelverarbeitung = computed(() => {
      return piniaStore.state.isDunkelverarbeitung;
    });

    const istEeKRabatt = computed(() => {
      if (gebaeudedaten.eekManuell) {
        return gebaeudedaten.eekManuell === "A" || gebaeudedaten.eekManuell === "A+";
      }
      if (gebaeudedaten.eek) {
        return gebaeudedaten.eek === "A" || gebaeudedaten.eek === "A+";
      }

      return false;
    });

    const preisDurchSanierungGesunken = computed(() => {
      const beitragVorKernsanierung = istFhkVerkaufsprodukt(piniaStore.state)
        ? piniaStore.state.ferienhaus.vgv.tarifinformationen.beitragVorKernsanierungGemZahlungsweise
        : piniaStore.state.tarifinformationen.beitragVorKernsanierungGemZahlungsweise;

      return beitragVorKernsanierung >= preisGemZahlungsweise.value;
    });

    const preisGemZahlungsweise = computed(() => {
      return !istFhkVerkaufsprodukt(piniaStore.state)
        ? piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].gemZahlungsweise
        : piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinien.length - 1].gemZahlungsweise;
    });

    const istKernsanierung = computed(() => {
      if (istFhkVerkaufsprodukt(piniaStore.state)) {
        const { enthaeltVGV } = storeUtils();

        return enthaeltVGV(piniaStore.state) && piniaStore.state.ferienhaus.vgv.tarifinformationen.kernsanierung;
      }

      return piniaStore.state.tarifinformationen.kernsanierung;
    });

    const istSbVgvFehlendeVorversicherung = computed(() => {
      const { enthaeltVGV } = storeUtils();
      let vertragsende = piniaStore.state.vorversicherung.vertragEnde;
      let versbeginn = piniaStore.state.tarifinformationen.versicherungsbeginn;
      let selbstbeteiligung = piniaStore.state.tarifinformationen.extraSelbstbeteiligung;
      let vorversicherungVorhanden = piniaStore.state.vorversicherung.vorhanden;

      if (istFhkVerkaufsprodukt(piniaStore.state) && enthaeltVGV(piniaStore.state)) {
        vorversicherungVorhanden = piniaStore.state.ferienhaus.vgv.vorversicherung.vorhanden;
        vertragsende = piniaStore.state.ferienhaus.vgv.vorversicherung.vertragEnde;
        versbeginn = piniaStore.state.ferienhaus.vgv.tarifinformationen.versicherungsbeginn;
        selbstbeteiligung = piniaStore.state.ferienhaus.vgv.tarifinformationen.extraSelbstbeteiligung;
      }

      return (
        ExtraSelbstbeteiligungService.vgvSelbstbeteiligungVorversicherungFehltPruefen(
          piniaStore.state.isMakler,
          vertragsende,
          vorversicherungVorhanden,
          versbeginn,
        ) &&
        selbstbeteiligung !== null &&
        selbstbeteiligung > 0
      );
    });

    const hoeheSbVgvFehlendeVorversicherung = computed(() => {
      if (istFhkVerkaufsprodukt(piniaStore.state)) {
        return piniaStore.state.ferienhaus.vgv.tarifinformationen.extraSelbstbeteiligung;
      }
      return piniaStore.state.tarifinformationen.extraSelbstbeteiligung;
    });

    const istSonstigesGewerbe = computed(() => {
      if (piniaStore.state.gebaeudedaten.gewerbeAnzahl !== null && piniaStore.state.gebaeudedaten.gewerbeAnzahl > 0) {
        for (let i = 0; i < piniaStore.state.gebaeudedaten.gewerbeAnzahl; i++) {
          if (piniaStore.state.gebaeudedaten.gewerbeListe[i].art === "99000") {
            return true;
          }
        }
      }

      return false;
    });

    const sfrAusVorbelegung = computed(() => {
      return (
        piniaStore.state.isMakler &&
        piniaStore.state.tarifinformationen.sfrAusVorbelegung &&
        piniaStore.state.tarifinformationen.sfr === 1
      );
    });

    return {
      props,
      piniaStore,
      formatCurrency,
      wechsleSichtbarkeit,
      beitragVorKernsanierungJaehrlich,
      beitragVorKernsanierungGemZahlungsweise,
      keineDunkelverarbeitungText,
      zeigeHinweisSchadenfreiBonus,
      zeigeAnpassungsfaktorMeldung,
      istHinweisFuerBlBh,
      istDunkelverarbeitung,
      istEeKRabatt,
      istKernsanierung,
      istSbVgvFehlendeVorversicherung,
      hoeheSbVgvFehlendeVorversicherung,
      istSonstigesGewerbe,
      preisGemZahlungsweise,
      preisDurchSanierungGesunken,
      istFhkVerkaufsprodukt,
      sfrAusVorbelegung,
    };
  },
});
</script>

<template>
  <div class="o-grid">
    <div class="o-grid__cell o-grid__cell--12 o-grid__cell--10-1024 infobox">
      <div v-if="zeigeHinweisSchadenfreiBonus && istDunkelverarbeitung" class="gev19-infobox--blue summary-label">
        {{
          piniaStore.state.isMakler === true
            ? "Sofern Ihr Versicherungsnehmer keine Schäden hat, profitiert dieser automatisch " +
              "vom Schadenfrei-Bonus (10%). Bitte beachten Sie, dass dieser Bonus beim ersten " +
              "Schaden nach der ersten Entschädigungszahlung entfällt."
            : "Sie profitieren vom Schadenfrei-Bonus 10 %, weil und solange Sie keine Schäden haben."
        }}
        <span
          v-if="!piniaStore.state.isMakler"
          class="c-notice-icon"
          aria-hidden="true"
          @click="wechsleSichtbarkeit('bonushilfe')"
        >
          <icon-sprite icon-name="icon-info-i" />
        </span>
        <Infotext
          role="tooltip"
          id="bonushilfe"
          name="bonushilfe"
          text="Bitte beachten Sie, dass dieser beim ersten Schaden nach der ersten
            Entschädigungszahlung entfällt."
        >
        </Infotext>
      </div>

      <div v-if="sfrAusVorbelegung" class="gev19-infobox-filled--blue u-color-white infoitem">
        SFR Stufe {{ piniaStore.state.tarifinformationen.sfrAusVorbelegung }} wurde berücksichtigt. Stufungsrelevante
        Schäden aus dem aktuellen Jahr werden automatisch zur nächsten Hauptfälligkeit ermittelt. Eine zusätzliche
        Erfassung von Schäden des aktuellen Jahres ist deshalb nicht notwendig.
      </div>

      <div v-if="istSonstigesGewerbe" class="gev19-infobox-filled--blue u-color-white infoitem">
        Aufgrund der angegebenen Gewerbeart erfolgt eine gesonderte Antragsprüfung. Der ausgewiesene Beitrag kann sich
        ggf. erhöhen.
      </div>

      <div v-if="istEeKRabatt" class="gev19-infobox-filled--blue u-color-white infoitem">
        Sie profitieren vom Energie-Bonus wegen der energieeffizienten Bauweise Ihres Gebäudes.
      </div>

      <div v-if="zeigeAnpassungsfaktorMeldung" class="gev19-infobox-filled--blue u-color-white infoitem">
        Aufgrund des Versicherungsbeginns im nächsten Jahr hat sich der Beitrag aufgrund des dann gültigen
        Anpassungsfaktors geändert.
      </div>

      <div
        v-if="
          (zusaetzlicheSbLw && istDunkelverarbeitung) ||
          (!piniaStore.state.isMakler && !istDunkelverarbeitung) ||
          istKernsanierung ||
          props.hinweistext ||
          istSbVgvFehlendeVorversicherung
        "
        class="gev19-infobox-filled--blue u-color-white infoitem"
      >
        <div v-if="zusaetzlicheSbLw && istDunkelverarbeitung">
          <strong>Hinweis:</strong> Die zusätzliche Selbstbeteiligung je Leitungswasser-Schaden beträgt
          <strong>750 €</strong>
        </div>
        <div v-if="istSbVgvFehlendeVorversicherung">
          <strong>Hinweis:</strong> Aufgrund der fehlenden Vorversicherung wird ein beitragsneutraler Selbstbehalt für
          die Grundgefahren in Höhe von
          <strong>{{ formatCurrency(hoeheSbVgvFehlendeVorversicherung) }}</strong>
          vereinbart. Dieser kann bei schadenfreiem Verlauf nach einem Jahr geprüft werden.
        </div>

        <div v-if="!piniaStore.state.isMakler && !istDunkelverarbeitung">
          <strong>Hinweis:</strong> {{ keineDunkelverarbeitungText }}
        </div>

        <div v-if="props.hinweistext" v-html="props.hinweistext" />

        <div
          v-if="
            (istKernsanierung && !piniaStore.state.isMakler) ||
            (istFhkVerkaufsprodukt(piniaStore.state) && istKernsanierung)
          "
        >
          <span v-if="preisDurchSanierungGesunken"
            >Aufgrund der durchgeführten Sanierungen können wir Ihnen ein noch attraktiveres Angebot machen.</span
          >
          <span v-else>Aufgrund Ihrer weiteren Angaben hat sich Ihr Preis verändert.</span>

          <div v-if="piniaStore.state.tarifinformationen.anpassungZahlungsweise">
            Dadurch war eine Anpassung Ihrer Zahlungsweise notwendig.<br />
            <div>
              <strong
                >Der jährliche Beitrag ändert sich von
                {{ beitragVorKernsanierungJaehrlich }}
                auf
                {{ formatCurrency(preisGemZahlungsweise) }}.
              </strong>
            </div>
          </div>
          <div v-else>
            <div v-if="!istFhkVerkaufsprodukt(piniaStore.state)">
              <strong>Ihr ursprünglicher Preis: {{ beitragVorKernsanierungGemZahlungsweise }}.</strong>
            </div>
            <div v-else>
              <strong
                >Ihr ursprünglicher Preis:
                {{
                  formatCurrency(
                    piniaStore.state.ferienhaus.vgv.tarifinformationen.beitragVorKernsanierungGemZahlungsweise,
                  )
                }}.</strong
              >
            </div>
          </div>
        </div>
      </div>
      <div
        class="gev19-infobox-filled--blue u-color-white infoitem"
        v-if="piniaStore.state.isMakler && istHinweisFuerBlBh"
      >
        <strong>Hinweis:</strong> Aufgrund Ihrer Angaben prüfen wir Ihren Antrag individuell. Hierdurch können sich
        Abweichungen ergeben.
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.infoitem {
  margin-top: 0 !important;
}

.infobox {
  padding-top: 15px !important;
}
</style>
